import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  GET_ACCOUNT_DETAILS,
  ADD_USER_TO_ACCOUNT,
  REMOVE_USER_FROM_ACCOUNT,
  UPDATE_USER_ROLE,
  GET_USER_PROFILE,
} from '../../graphql';
import { GetAccountDetailsResponse } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

type UserRole = 'admin' | 'member';

export function TeamSettings() {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('member');
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState<boolean>(false);
  const [userToRemove, setUserToRemove] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [isChangeRoleOpen, setIsChangeRoleOpen] = useState<boolean>(false);
  const [userToChangeRole, setUserToChangeRole] = useState<{
    id: string;
    email: string;
    currentRole: string;
    newRole: UserRole;
  } | null>(null);
  const [isAdminConfirmOpen, setIsAdminConfirmOpen] = useState<boolean>(false);

  const { accountId, selectedAccount, isLoading: accountsLoading, refetchAccounts } = useAccount();

  const {
    data: accountData,
    loading,
    error,
    refetch,
  } = useQuery<GetAccountDetailsResponse>(GET_ACCOUNT_DETAILS, {
    variables: { id: accountId },
    skip: !accountId,
    onCompleted: (data) => {
      console.log('Account Query Completed:', data);
    },
    onError: (error) => {
      console.error('Account Query Error:', error);
    },
  });

  const [addUserToAccount] = useMutation(ADD_USER_TO_ACCOUNT, {
    onCompleted: () => {
      handleCloseDialog();
      refetch();
      refetchAccounts();
    },
    onError: (error) => {
      console.error('Error adding user to account:', error);
    },
  });

  const [removeUserFromAccount, { loading: isRemovingUser }] = useMutation(
    REMOVE_USER_FROM_ACCOUNT,
    {
      onCompleted: () => {
        setIsRemoveConfirmOpen(false);
        setUserToRemove(null);
        refetch();
        refetchAccounts();
      },
      onError: (error) => {
        console.error('Error removing user from account:', error);
        // You might want to show an error toast/notification here
      },
    }
  );

  const [updateUserRole, { loading: isUpdatingRole }] = useMutation(UPDATE_USER_ROLE, {
    onCompleted: () => {
      setIsChangeRoleOpen(false);
      setIsAdminConfirmOpen(false);
      setUserToChangeRole(null);
      refetch();
      refetchAccounts();
    },
    onError: (error) => {
      console.error('Error updating user role:', error);
      // You might want to show an error toast/notification here
    },
  });

  // Get current user's email to prevent self-removal
  const { data: userProfileData } = useQuery<UserProfileData>(GET_USER_PROFILE);
  const currentUserEmail = userProfileData?.me?.email;

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewUserEmail('');
    setNewUserRole('member');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUserEmail('');
    setNewUserRole('member');
  };

  const handleInviteUser = async () => {
    if (!newUserEmail.trim() || !accountId) return;

    try {
      await addUserToAccount({
        variables: {
          input: {
            accountId: accountId,
            userId: newUserEmail,
            role: newUserRole,
          },
        },
      });
    } catch (error) {
      console.error('Error inviting user:', error);
    }
  };

  if (accountsLoading || loading) return <p className="ink-lede">Loading…</p>;
  if (error) return <p className="ink-lede">Could not load the team.</p>;

  const teamMembers = accountData?.account?.users || [];
  const memberCount = teamMembers.length;

  // Get current plan from account data
  const currentPlan = accountData?.account?.plan || selectedAccount?.plan || '';

  const handleRemoveMember = (userAccount: { id: string; user: { email: string } }) => {
    setUserToRemove({
      id: userAccount.id,
      email: userAccount.user.email,
    });
    setIsRemoveConfirmOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!accountId || !userToRemove) {
      console.error('Account ID and user to remove are required');
      return;
    }

    try {
      await removeUserFromAccount({
        variables: {
          accountId: accountId,
          id: userToRemove.id,
        },
      });
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };

  const handleChangeRole = (userAccount: { id: string; user: { email: string }; role: string }) => {
    setUserToChangeRole({
      id: userAccount.id,
      email: userAccount.user.email,
      currentRole: userAccount.role,
      newRole: userAccount.role === 'admin' ? 'admin' : 'member',
    });
    setIsChangeRoleOpen(true);
  };

  const handleSaveRole = async () => {
    if (!accountId || !userToChangeRole) {
      console.error('Account ID and user to change role are required');
      return;
    }

    // If role hasn't changed, just close the dialog
    if (userToChangeRole.currentRole === userToChangeRole.newRole) {
      setIsChangeRoleOpen(false);
      setUserToChangeRole(null);
      return;
    }

    // If changing from admin to member, show confirmation
    if (userToChangeRole.currentRole === 'admin' && userToChangeRole.newRole !== 'admin') {
      setIsAdminConfirmOpen(true);
      return;
    }

    // Otherwise, proceed with the update
    await executeRoleUpdate();
  };

  const executeRoleUpdate = async () => {
    if (!accountId || !userToChangeRole) {
      return;
    }

    try {
      await updateUserRole({
        variables: {
          accountId: accountId,
          id: userToChangeRole.id,
          role: userToChangeRole.newRole,
        },
      });
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  const handleConfirmAdminChange = async () => {
    setIsAdminConfirmOpen(false);
    await executeRoleUpdate();
  };

  const seatCaps: Record<string, number> = { Enterprise: 10, Pro: 3, Basic: 1 };
  const seatCap = seatCaps[currentPlan] || 10;

  return (
    <div>
      <div className="ink-conn-head" style={{ marginTop: 0, alignItems: 'center' }}>
        <h1 className="ink-h1" style={{ margin: 0 }}>
          Team
        </h1>
        <Dialog
          open={openDialog}
          onOpenChange={(open) => {
            if (open) handleOpenDialog();
            else handleCloseDialog();
          }}
        >
          <DialogTrigger asChild>
            <button type="button" className="ink-btn ink-btn-primary">
              Invite
            </button>
          </DialogTrigger>
          <DialogContent className="bg-core-dark">
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to add a new member to your team.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="email@acme.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  autoFocus
                  className="bg-form-dark text-cmp-ink"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={newUserRole}
                  onValueChange={(value) => setNewUserRole(value as UserRole)}
                >
                  <SelectTrigger id="invite-role" className="bg-form-dark text-cmp-ink">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-core-dark hover:bg-form-dark text-cmp-ink">
                    <SelectItem
                      value="admin"
                      className="bg-core-dark hover:bg-form-dark text-cmp-ink"
                    >
                      Admin
                    </SelectItem>
                    <SelectItem
                      value="member"
                      className="bg-core-dark hover:bg-form-dark text-cmp-ink"
                    >
                      Member
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground">Admins can manage team settings and members</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleCloseDialog}
                className="bg-button-secondary text-button-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteUser}
                disabled={!newUserEmail.trim()}
                className="bg-button-primary text-button-primary"
              >
                <Mail className="size-4 mr-2" />
                Send Invitation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <p className="ink-lede">
        {memberCount} of {seatCap} seats used
      </p>

      <div className="ink-team">
        {teamMembers.length === 0 ? (
          <p className="ink-lede" style={{ marginTop: 16 }}>
            No one on the team yet.
          </p>
        ) : (
          teamMembers.map((userAccount) => {
            const role = userAccount.role === 'admin' ? 'Admin' : 'Member';
            const name = userAccount.user.email.split('@')[0];
            const joined = new Date(userAccount.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            });
            const isCurrentUser = userAccount.user.email === currentUserEmail;
            const canRemoveMember = !isCurrentUser && memberCount > 1;
            const initials = name.slice(0, 2).toUpperCase();

            return (
              <div className="ink-team-row" key={userAccount.user.id}>
                <span className="ink-team-avatar">{initials}</span>
                <div>
                  <div className="ink-team-name">{name}</div>
                  <div className="ink-team-email">{userAccount.user.email}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="ink-team-role">
                      {role} ▾
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-core-dark">
                    {userAccount.id ? (
                      <DropdownMenuItem onClick={() => handleChangeRole(userAccount)}>
                        Change role
                      </DropdownMenuItem>
                    ) : null}
                    {canRemoveMember && userAccount.id ? (
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => handleRemoveMember(userAccount)}
                      >
                        Remove
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
                <span className="ink-team-since">Since {joined}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={isRemoveConfirmOpen} onOpenChange={setIsRemoveConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.email}</strong> from your team?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsRemoveConfirmOpen(false);
                setUserToRemove(null);
              }}
              disabled={isRemovingUser}
              className="bg-button-primary text-button-primary"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRemove}
              disabled={isRemovingUser}
              className="bg-button-secondary text-button-secondary"
            >
              {isRemovingUser ? 'Removing...' : 'Remove Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={isChangeRoleOpen} onOpenChange={setIsChangeRoleOpen}>
        <DialogContent className="bg-core-dark">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Change the role for <strong>{userToChangeRole?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="change-role">Role</Label>
              <Select
                value={userToChangeRole?.newRole || 'member'}
                onValueChange={(value) => {
                  if (userToChangeRole) {
                    setUserToChangeRole({
                      ...userToChangeRole,
                      newRole: value as UserRole,
                    });
                  }
                }}
              >
                <SelectTrigger id="change-role" className="bg-form-dark text-cmp-ink">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-core-dark">
                  <SelectItem value="admin" className="hover:bg-form-dark text-cmp-ink">
                    Admin
                  </SelectItem>
                  <SelectItem value="member" className="hover:bg-form-dark text-cmp-ink">
                    Member
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-sm">
                Admins can manage team settings and members
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsChangeRoleOpen(false);
                setUserToChangeRole(null);
              }}
              disabled={isUpdatingRole}
              className="bg-button-secondary text-button-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={isUpdatingRole}
              className="bg-button-primary text-button-primary"
            >
              {isUpdatingRole ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Change Confirmation Dialog */}
      <Dialog open={isAdminConfirmOpen} onOpenChange={setIsAdminConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Role Change</DialogTitle>
            <DialogDescription>
              You are about to change <strong>{userToChangeRole?.email}</strong> from{' '}
              <strong>Admin</strong> to <strong>Member</strong>. This will remove their ability to
              manage team settings and members. Are you sure you want to continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsAdminConfirmOpen(false);
              }}
              disabled={isUpdatingRole}
              className="bg-button-primary text-button-primary"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmAdminChange}
              disabled={isUpdatingRole}
              className="bg-button-secondary text-button-secondary"
            >
              {isUpdatingRole ? 'Updating...' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
